(ns leg.mui
  "The components the window is drawn with, and the two themes they are
  given.

  One component at a time, never the whole package: a release is compiled
  with `:simple`, so what is required here is what is shipped to the phone.

  What Material is here for is the behaviour underneath - a dialog that
  takes the focus and hands it back, a list row that says it was touched, a
  field that knows it is a field. The look is `leg.theme`'s: a reader is a
  page of somebody else's prose, and Material's shouting blue is not part
  of it."
  (:refer-clojure :exclude [Box List])
  (:require-macros [kc.core :refer [def-ui $]])
  (:require ["@mui/material/styles" :as mui-styles]
            ["@mui/material/useMediaQuery$default" :as mui-use-media-query]
            ["@mui/material/CssBaseline$default" :as mui-css-baseline]
            ["@mui/material/Alert$default" :as mui-alert]
            ["@mui/material/Box$default" :as mui-box]
            ["@mui/material/Button$default" :as mui-button]
            ["@mui/material/Chip$default" :as mui-chip]
            ["@mui/material/CircularProgress$default" :as mui-circular-progress]
            ["@mui/material/Dialog$default" :as mui-dialog]
            ["@mui/material/DialogActions$default" :as mui-dialog-actions]
            ["@mui/material/DialogContent$default" :as mui-dialog-content]
            ["@mui/material/DialogTitle$default" :as mui-dialog-title]
            ["@mui/material/Divider$default" :as mui-divider]
            ["@mui/material/IconButton$default" :as mui-icon-button]
            ["@mui/material/LinearProgress$default" :as mui-linear-progress]
            ["@mui/material/List$default" :as mui-list]
            ["@mui/material/ListItemButton$default" :as mui-list-item-button]
            ["@mui/material/ListItemText$default" :as mui-list-item-text]
            ["@mui/material/ListSubheader$default" :as mui-list-subheader]
            ["@mui/material/Menu$default" :as mui-menu]
            ["@mui/material/MenuItem$default" :as mui-menu-item]
            ["@mui/material/TextField$default" :as mui-text-field]
            ["@mui/material/Tooltip$default" :as mui-tooltip]
            ["@mui/material/Typography$default" :as mui-typography]
            [leg.theme :as theme]))

;;; The vocabulary. Named once here so a screen says `mui/Button` and never
;;; a module path.

(def Alert mui-alert)
(def Box mui-box)
(def Button mui-button)
(def Chip mui-chip)
(def CircularProgress mui-circular-progress)
(def Dialog mui-dialog)
(def DialogActions mui-dialog-actions)
(def DialogContent mui-dialog-content)
(def DialogTitle mui-dialog-title)
(def Divider mui-divider)
(def IconButton mui-icon-button)
(def LinearProgress mui-linear-progress)
(def List mui-list)
(def ListItemButton mui-list-item-button)
(def ListItemText mui-list-item-text)
(def ListSubheader mui-list-subheader)
(def Menu mui-menu)
(def MenuItem mui-menu-item)
(def TextField mui-text-field)
(def Tooltip mui-tooltip)
(def Typography mui-typography)

(defn sx
  "A style for MUI, as a map. MUI reads a plain object and nothing else, and
  a map that went unconverted is a style that silently does nothing - so
  every `:sx` in this window goes through here and it is visible when one
  did not."
  [m]
  (clj->js m))

(def one-pane-query
  "The width at which there is room for one pane and not three. Named here
  because three other places have to agree with it to the character: the
  two blocks in `index.html` that decide what a change of page looks like,
  and `leg.vt`, which may only push a page sideways where the page is the
  whole screen."
  "(max-width: 56rem)")

(defn use-narrow?
  "Whether there is room for more than one pane. A phone is not a small
  desktop: the three columns become one screen at a time, and which one is
  shown is what the person last opened (see leg.app)."
  []
  (mui-use-media-query one-pane-query))

(defn one-pane?
  "The same question asked outside a component, once, by something that is
  not drawing anything and has nothing to re-render: the page a finger may
  push sideways (`leg.vt`), the pane a window comes up in
  (`leg.state/start-pane!`). `use-narrow?` is the hook and is what a
  component uses - it answers again when the window is resized, which this
  cannot."
  []
  (boolean (and (exists? js/matchMedia)
                (.-matches (js/matchMedia one-pane-query)))))

(defn- mk-theme [mode]
  (let [c (theme/colors mode)
        line (str "1px solid " (:line c))]
    (mui-styles/createTheme
     (clj->js
      {:palette
       {:mode       mode
        :background {:default (:bg c) :paper (:panel c)}
        :text       {:primary (:fg c) :secondary (:dim c)}
        :primary    {:main (:accent c)}
        :error      {:main (:warn c)}
        :divider    (:line c)}

       :shape {:borderRadius 6}

       ;; The furniture's face by default, and the display serif only where
       ;; something is a headline. A component that was given no variant is
       ;; a label or a button, and setting prose in Playfair by accident is
       ;; how a page ends up unreadable.
       :typography
       {:fontFamily theme/font
        :fontSize   15
        :h5         {:fontFamily theme/font-head :fontWeight 700
                     :letterSpacing "-.015em"}
        :h6         {:fontFamily theme/font-head :fontWeight 700
                     :letterSpacing "-.01em"}
        :button     {:textTransform "none" :fontWeight 500
                     :letterSpacing ".01em"}}

       :components
       {;; The surfaces the browser draws and not this window: the grain,
        ;; the selection, the scrollbar, the focus ring. They belong to the
        ;; theme like everything else - a page that themes its text and
        ;; leaves the browser's blue in the selection is a page with
        ;; somebody else's colour in the middle of it.
        ;;
        ;; The grain is here rather than in a media query so the choice
        ;; follows the setting rather than the machine, for somebody whose
        ;; two disagree.
        :MuiCssBaseline
        {:styleOverrides
         {:body {"--leser-grain" (if (= "dark" mode)
                                   "var(--leser-grain-dark)"
                                   "var(--leser-grain-light)")
                 :scrollbarColor (str (:line c) " transparent")}
          "::selection" {:background (:sel c) :color (:fg c)}
          ;; One focus ring for the whole window, in the accent, and only
          ;; for a keyboard: `:focus-visible` is the state a mouse click
          ;; does not enter. Two pixels and an offset, because the ring has
          ;; to be visible against newsprint as well as against ink.
          "*:focus-visible" {:outline (str "2px solid " (:accent c))
                             :outlineOffset "1px"}}}

        :MuiPaper {:styleOverrides {:root {:backgroundImage "none"}}}

        ;; Small on a desktop, where the pointer is a pixel wide; forty-four
        ;; on anything a finger touches. The small size is what keeps a
        ;; toolbar a line of text rather than a row of tiles, and a finger
        ;; does not care what a mouse needed.
        :MuiButton {:defaultProps {:size "small"}
                    :styleOverrides
                    {:root {:minWidth 0
                            "@media (pointer: coarse)" {:minHeight "2.75rem"}}}}

        :MuiChip {:defaultProps   {:size "small" :variant "outlined"}
                  :styleOverrides {:root {:borderColor (:line c)
                                          :color (:dim c)}}}

        ;; A row in the list is a line of text, not a form control: the
        ;; padding is a reader's, and the thing that says which one is
        ;; open is a bar at its edge rather than a wash of colour.
        :MuiListItemButton
        {:styleOverrides
         {:root {:alignItems "flex-start"
                 :paddingLeft ".75rem"
                 :paddingRight ".75rem"
                 "&:hover"              {:backgroundColor (:hover c)}
                 "&.Mui-selected"       {:backgroundColor (:cursor c)
                                         :boxShadow (str "inset 2px 0 0 " (:accent c))}
                 "&.Mui-selected:hover" {:backgroundColor (:cursor c)}}}}

        :MuiListItemText {:styleOverrides {:root {:margin 0}}}

        :MuiListSubheader
        {:styleOverrides {:root {:background "transparent"
                                 :color (:dim c)
                                 :fontSize ".75rem"
                                 :letterSpacing ".08em"
                                 :textTransform "uppercase"
                                 :lineHeight 2.4}}}

        ;; What is drawn over the page has to look drawn over the page.
        ;; Material says that with elevation, which is a shadow and - in
        ;; the dark - a white film over the paper; the film is off here
        ;; (`MuiPaper`, `backgroundImage: none`) because it greys a warm
        ;; palette, and the shadow alone is not enough on newsprint, where
        ;; the surface underneath is already the same cream. So all three:
        ;; a surface a shade away from the page (`:raised`), a line around
        ;; it like every other edge in this window, and a shadow in the
        ;; page's own ink rather than the browser's neutral grey - close
        ;; and tight underneath, wide and soft further out, which is what
        ;; a sheet lying on a desk does to the light.
        :MuiMenu
        {:styleOverrides
         {:paper {:backgroundColor (:raised c)
                  :border line
                  :borderRadius "8px"
                  :boxShadow (str "0 1px 2px " (:shade c)
                                  ", 0 10px 30px -6px " (:shade c))}
          :list {:paddingTop 0 :paddingBottom ".25rem"}}}

        ;; A line in a menu is pressed with the same thumb as a row in the
        ;; list, and a thumb needs the same forty-four pixels here. The
        ;; warm wash on both states is the list's: Material's own is a
        ;; neutral grey, and a grey band across a cream menu is the one
        ;; thing on the page that is not in the palette. `focusVisible` as
        ;; well as `hover`, because a menu puts the focus on its first line
        ;; the moment it opens - it is marked here whether it was reached
        ;; by a key or by a thumb.
        :MuiMenuItem
        {:styleOverrides
         {:root {:paddingTop ".5rem" :paddingBottom ".5rem"
                 :minHeight 0
                 "&:hover" {:backgroundColor (:hover c)}
                 "&.Mui-focusVisible" {:backgroundColor (:hover c)}
                 "@media (pointer: coarse)" {:minHeight "2.75rem"}}}}

        :MuiDialog {:defaultProps   {:fullWidth true :maxWidth "sm"}
                    :styleOverrides {:paper {:border line
                                             :boxShadow (str "0 2px 4px " (:shade c)
                                                             ", 0 24px 64px -12px " (:shade c))}}}
        :MuiDialogTitle {:styleOverrides {:root {:fontSize "1.05rem" :fontWeight 600}}}}}))))

(def themes
  "Both, built once. Building a theme is not free and the window switches
  between them - at sunset, if the machine does."
  {"light" (mk-theme "light") "dark" (mk-theme "dark")})

(def-ui Frame
  "Everything the window draws, inside the one theme.

  `CssBaseline` as well, because it is what makes the page the colour of
  the theme rather than the browser's white - and the page is what is
  behind a phone's bounce at the end of a scroll."
  [mode ***]
  ($ mui-styles/ThemeProvider {:theme (get themes mode (get themes "light"))}
     ($ mui-css-baseline)
     ***))
